import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

export const EASE = [0.16, 1, 0.3, 1];

/* Line-by-line masked reveal for headings */
export function MaskText({
  lines,
  as = "h2",
  className = "",
  lineClassName = "",
  animateOnLoad = false,
  delay = 0,
  stagger = 0.1,
}) {
  const Comp = motion[as] || motion.h2;
  const container = {
    hidden: {},
    visible: { transition: { staggerChildren: stagger, delayChildren: delay } },
  };
  const child = {
    hidden: { y: "115%" },
    visible: { y: "0%", transition: { duration: 0.9, ease: EASE } },
  };
  const activation = animateOnLoad
    ? { initial: "hidden", animate: "visible" }
    : { initial: "hidden", whileInView: "visible", viewport: { once: true, amount: 0.2 } };
  return (
    <Comp className={className} aria-label={lines.join(" ")} variants={container} {...activation}>
      {lines.map((line, i) => (
        <span key={i} className="block overflow-hidden pb-[0.08em]">
          <motion.span className={`block ${lineClassName}`} variants={child}>
            {line}
          </motion.span>
        </span>
      ))}
    </Comp>
  );
}

/* Generic scroll reveal */
export function Reveal({ children, className = "", delay = 0, y = 26, as = "div" }) {
  const Comp = motion[as] || motion.div;
  return (
    <Comp
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-8% 0px" }}
      transition={{ duration: 0.85, ease: EASE, delay }}
    >
      {children}
    </Comp>
  );
}

/* Parallax image inside a clipped frame */
export function ParallaxImage({
  src,
  alt,
  className = "",
  imgClassName = "",
  amount = 50,
  priority = false,
}) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [-amount, amount]);
  return (
    <div ref={ref} className={`overflow-hidden ${className}`}>
      <motion.img
        style={{ y }}
        src={src}
        alt={alt}
        loading={priority ? "eager" : "lazy"}
        decoding="async"
        className={`h-full w-full scale-[1.12] object-cover ${imgClassName}`}
      />
    </div>
  );
}
